/**
 * CR-7139: an NCR root-cause chain holds as many Whys as the investigation
 * needs. It starts as five; the user may remove Whys or add more. The API
 * reads and writes each chain as one ordered list — `whys` for the Occurrence
 * chain, `escape_whys` for the Escape chain (CR-7353).
 */

import type { NCREditFormData } from './types';

/** How many Whys a chain starts with. */
export const INITIAL_WHYS = 5;
/** The most Whys a chain may hold (the API refuses more). */
export const MAX_WHYS = 10;

export type WhyChain = 'whys' | 'escape_whys';

/**
 * The chain's Whys as the form holds them.
 *
 * @param formData - the NCR edit form state
 * @param chain - `whys` or `escape_whys`
 * @returns the Whys in order; the five fixed Whys when no list has loaded
 */
export function chainWhys(formData: NCREditFormData, chain: WhyChain): string[] {
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  const whys = formData[chain];
  if (Array.isArray(whys) && whys.length > 0) return whys.map(text);
  return Array.from({ length: INITIAL_WHYS }, (_, i) => text(formData[whyFieldName(chain, i + 1)]));
}

/**
 * The field name that identifies Why `n` of a chain in validation messages.
 *
 * @param chain - `whys` or `escape_whys`
 * @param n - the Why's number, from 1
 * @returns e.g. `why_1`, `escape_why_2`
 */
export function whyFieldName(chain: WhyChain, n: number): string {
  return `${chain === 'escape_whys' ? 'escape_' : ''}why_${n}`;
}

/**
 * The form value behind an RCA field name, reading `why_N` / `escape_why_N`
 * from the chain's list.
 *
 * @param formData - the NCR edit form state
 * @param field - an RCA field name, e.g. `why_1` or `selected_root_cause`
 * @returns the value the user has entered for that field
 */
export function rcaFieldValue(formData: NCREditFormData, field: string): unknown {
  const match = /^(escape_)?why_(\d+)$/.exec(field);
  if (!match) return formData[field];
  return chainWhys(formData, match[1] ? 'escape_whys' : 'whys')[Number(match[2]) - 1];
}
